import {
  Check as CheckIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenInNewIcon,
  QrCode2 as QrCodeIcon,
} from '@mui/icons-material';
import { Alert, Box, Button, Link, Paper, TextField, Typography } from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { heIL } from '@mui/x-date-pickers/locales';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dayjs } from 'dayjs';
import 'dayjs/locale/he';
import { QRCodeSVG } from 'qrcode.react';
import React, { useRef, useState } from 'react';
import { useDirection } from '../../context/DirectionContext';
import { translate as tr } from '../../i18n/translations';
import apiService from '../../services/api';
import { CycleStatus, RegistrationState } from '../../types/api';
import ErrorAlert from '../Common/ErrorAlert';

const QR_IMAGE_SIZE = 512;

/** Rasterizes the QR SVG to a PNG with a white margin, for pasting into chats and documents. */
const qrToPng = (svg: SVGSVGElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.setAttribute('width', String(QR_IMAGE_SIZE));
    clone.setAttribute('height', String(QR_IMAGE_SIZE));
    const img = new Image();
    img.onload = () => {
      const margin = QR_IMAGE_SIZE / 16;
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = QR_IMAGE_SIZE + margin * 2;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas not available'));
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, margin, margin, QR_IMAGE_SIZE, QR_IMAGE_SIZE);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('PNG encoding failed'))), 'image/png');
    };
    img.onerror = () => reject(new Error('Could not load QR SVG'));
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(clone))}`;
  });

interface Props {
  cycleId: string;
  cycleStatus: CycleStatus;
}

const RegistrationTab: React.FC<Props> = ({ cycleId, cycleStatus }) => {
  const { lang } = useDirection();
  const queryClient = useQueryClient();
  const queryKey = ['cycleRegistration', cycleId];
  const [closesAt, setClosesAt] = useState<Dayjs | null>(null);
  const [cap, setCap] = useState('');
  const [copied, setCopied] = useState<'link' | 'qr' | null>(null);
  const qrRef = useRef<HTMLDivElement>(null);
  const registrationUrl = process.env.REACT_APP_REGISTRATION_URL;

  // The clipboard needs a secure context (https or localhost); elsewhere the copy silently does nothing.
  const copy = async (what: 'link' | 'qr') => {
    try {
      if (what === 'link') {
        if (!registrationUrl) return;
        await navigator.clipboard.writeText(registrationUrl);
      } else {
        const svg = qrRef.current?.querySelector('svg');
        if (!svg) return;
        // Pass the PNG as a promise so the write starts inside the click (required by Safari).
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': qrToPng(svg) })]);
      }
    } catch {
      return;
    }
    setCopied(what);
    setTimeout(() => setCopied(null), 2000);
  };

  const { data, error } = useQuery<RegistrationState>({
    queryKey,
    queryFn: () => apiService.getCycleRegistration(cycleId),
    refetchInterval: (query) => (query.state.data?.open ? 10_000 : false),
  });

  const onSuccess = (next: RegistrationState) => {
    queryClient.setQueryData(queryKey, next);
    queryClient.invalidateQueries({ queryKey });
  };

  const openMutation = useMutation({
    mutationFn: () => {
      const body: { closesAt?: string; cap?: number } = {};
      if (closesAt) body.closesAt = closesAt.toISOString();
      if (cap) body.cap = parseInt(cap, 10);
      return apiService.openCycleRegistration(cycleId, body);
    },
    onSuccess,
  });
  const closeMutation = useMutation({
    mutationFn: () => apiService.closeCycleRegistration(cycleId),
    onSuccess,
  });

  const isOpen = !!data?.open;
  const mutationError = openMutation.error || closeMutation.error;

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>{tr('reg.title', lang)}</Typography>

      {error && <ErrorAlert error={error} />}
      {mutationError && <ErrorAlert error={mutationError} />}

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          {isOpen ? tr('reg.status.open', lang) : tr('reg.status.closed', lang)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {tr('reg.registered', lang)}:{' '}
          <Box component="span" data-testid="reg-count" sx={{ fontWeight: 700 }}>
            {data?.registeredCount ?? 0}
          </Box>
          {isOpen && data?.cap != null && ` / ${data.cap}`}
        </Typography>
        {isOpen && data?.closesAt && (
          <Typography variant="body2" color="text.secondary">
            {tr('reg.closesOn', lang)}: {new Date(data.closesAt).toLocaleString()}
          </Typography>
        )}

        {!isOpen && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={lang}
              localeText={lang === 'he' ? heIL.components.MuiLocalizationProvider.defaultProps.localeText : undefined}
            >
              <DateTimePicker
                label={tr('reg.closesAt', lang)}
                value={closesAt}
                onChange={setClosesAt}
                disablePast
                ampm={false}
                closeOnSelect={false}
                slotProps={{
                  textField: { id: 'reg-closes-at', size: 'small' },
                  actionBar: { actions: ['clear', 'cancel', 'accept'] },
                }}
              />
            </LocalizationProvider>
            <TextField
              id="reg-cap"
              type="number"
              label={tr('reg.cap', lang)}
              value={cap}
              onChange={(e) => setCap(e.target.value)}
              slotProps={{ htmlInput: { min: 1, step: 1 } }}
              size="small"
            />
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          {isOpen ? (
            <Button
              variant="outlined"
              color="error"
              onClick={() => closeMutation.mutate()}
              disabled={closeMutation.isPending}
            >
              {tr('reg.close', lang)}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => openMutation.mutate()}
              disabled={cycleStatus === CycleStatus.CLOSED || openMutation.isPending}
            >
              {tr('reg.open', lang)}
            </Button>
          )}
        </Box>
        {cycleStatus === CycleStatus.CLOSED && !isOpen && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {tr('reg.cycleClosed', lang)}
          </Typography>
        )}
      </Paper>

      {isOpen &&
        (registrationUrl ? (
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ mb: 1 }}>{tr('reg.scanToRegister', lang)}</Typography>
            <Box ref={qrRef} data-testid="reg-qr" sx={{ display: 'inline-block', p: 1, bgcolor: '#fff' }}>
              <QRCodeSVG value={registrationUrl} size={192} role="img" aria-label={tr('reg.scanToRegister', lang)} />
            </Box>
            <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-all' }}>
              <Link href={registrationUrl} target="_blank" rel="noopener noreferrer">{registrationUrl}</Link>
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mt: 1.5 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={copied === 'qr' ? <CheckIcon /> : <QrCodeIcon />}
                onClick={() => copy('qr')}
              >
                {copied === 'qr' ? tr('reg.copied', lang) : tr('reg.copyQr', lang)}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={copied === 'link' ? <CheckIcon /> : <CopyIcon />}
                onClick={() => copy('link')}
              >
                {copied === 'link' ? tr('reg.copied', lang) : tr('reg.copyLink', lang)}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                href={registrationUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {tr('reg.openPage', lang)}
              </Button>
            </Box>
          </Paper>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>{tr('reg.noUrl', lang)}</Alert>
        ))}

      <Typography variant="caption" color="text.secondary">{tr('reg.final', lang)}</Typography>
    </Box>
  );
};

export default RegistrationTab;
